/**
 * Step 2 Mobile: People
 * Person cards with expandable State Pension and Tax-Free Cash sections.
 * Retirement age shown as a slider (55-75).
 */

import { useEffect, useState } from 'react';
import { useForm, type UseFormRegister, type FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { type Step2FormData, Step2Schema, type PersonData, getMinimumPensionAge, type AllStepFormData } from '../../types/wizard';
import { getStatePensionAge, STATE_PENSION_ANNUAL, STATE_PENSION_TAX_YEAR } from '../../constants/retirementBenchmarks';
import { priorIncomeAppliesToPlanYear as computePriorIncomeAppliesToPlanYear } from '../../utils/taxYear';
import { seedPriorDrawdownIncome, sumPriorIncomeBreakdown, numberOrZero, stripDerivedPriorIncomeTotal } from '../../utils/priorIncomeSeed';
import { sanitizeNaN } from '../../utils/sanitizeNaN';
import { formatCurrency } from '../../utils/formatters';
import { WizardMobileShell } from './WizardMobileShell';

/** Whole-pound default for the forecast field: the full new State Pension for the current tax year. */
const DEFAULT_STATE_PENSION_ANNUAL = Math.round(STATE_PENSION_ANNUAL);
const DEFAULT_STATE_PENSION_LABEL = `£${DEFAULT_STATE_PENSION_ANNUAL.toLocaleString('en-GB')} for ${STATE_PENSION_TAX_YEAR}`;

interface Step2Props {
  initialData?: Partial<AllStepFormData>;
  onNext: (data: Step2FormData) => void;
  onSave: (data: Step2FormData) => void;
  onBack?: () => void;
  lastSaved?: string;
}

function calcAge(dob: string): number {
  if (!dob) return 0;
  const today = new Date(), birth = new Date(dob);
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
}

function PersonCard({ personIndex, planType, personData, register, errors, onBlur, planAsOfDate }: Readonly<{
  personIndex: number;
  planType: string;
  personData: Partial<PersonData>;
  register: UseFormRegister<Step2FormData>;
  errors: FieldErrors<Step2FormData>;
  onBlur: () => void;
  planAsOfDate?: string;
}>) {
  const [expandedPension, setExpandedPension] = useState(false);
  const [expandedTaxFree, setExpandedTaxFree] = useState(false);
  // Blocking validation errors below live INSIDE these two collapsed
  // sections. If a section is collapsed when its error appears (e.g. the
  // user clears "Tax-Free Cash Already Taken", collapses the disclosure,
  // then presses Continue), the error mounts nowhere — Continue silently
  // does nothing, forever, with no explanation (review finding A). Force
  // the section open the moment it holds an error, so the message is
  // always visible without the user having to guess which collapsed
  // section is hiding it.
  const hasPensionError = Boolean(
    errors.persons?.[personIndex]?.statePensionAge || errors.persons?.[personIndex]?.currentStatePensionAnnual
  );
  const hasTaxFreeError = Boolean(
    errors.persons?.[personIndex]?.taxFreeLumpSumCap
    || errors.persons?.[personIndex]?.lsaUsedPrePlan
    || errors.persons?.[personIndex]?.taxFreeLumpSumPct
  );
  useEffect(() => {
    if (hasPensionError) setExpandedPension(true);
  }, [hasPensionError]);
  useEffect(() => {
    if (hasTaxFreeError) setExpandedTaxFree(true);
  }, [hasTaxFreeError]);
  const dob = personData?.dateOfBirth || '';
  const retDate = personData?.retirementDate || '';
  // Prior income fields (salary, drawdown, State Pension, rental, dividends,
  // pension contributions) only matter when the plan start date falls in the
  // same UK tax year the engine bands as Year 1 — see Step2People.tsx's
  // identical block for the full rationale.
  const priorIncomeAppliesToPlanYear = computePriorIncomeAppliesToPlanYear(planAsOfDate);
  const showPriorIncomeFields = Boolean(planAsOfDate && priorIncomeAppliesToPlanYear);
  // See Step2People.tsx's identical block for the full rationale: the engine
  // only takes ONE summed non-dividend field, but asking the user to sum
  // four figures in their head risks a wrong subtotal silently mis-banding
  // their Personal Allowance, so the wizard collects them separately and
  // sums here.
  // HIGH 1 (#1592 phase 2 review round 3): read each box through
  // numberOrZero/sumPriorIncomeBreakdown, not a hand-rolled `?? 0` sum.
  // `??` doesn't catch NaN — only null/undefined — so a blanked currency box
  // (react-hook-form holds NaN for it until blur) used to poison the WHOLE
  // subtotal into NaN, which the save-time NaN scrub then silently zeroed —
  // dropping every other box's real income, not just the blanked one. See
  // priorIncomeSeed.ts for the shared, NaN-safe version desktop already used
  // indirectly (its per-field local state can never hold NaN in the first
  // place — see withLocalAmountSync's comment in Step2People.tsx).
  const priorDividendIncome = numberOrZero(personData?.priorDividendIncome);
  const nonDividendSubtotal = sumPriorIncomeBreakdown(
    personData?.priorSalaryIncome,
    personData?.priorDrawdownIncome,
    personData?.priorStatePensionIncome,
    personData?.priorRentalIncome
  );
  const totalPriorIncome = nonDividendSubtotal + priorDividendIncome;
  // No sync effect writing this running total into `priorOtherTaxableIncome`
  // form state any more (phase 3, derived-total refactor) — that field is
  // never submitted or persisted at all; see stripDerivedPriorIncomeTotal
  // and priorIncomeSeed.ts's file comment.

  const age = calcAge(dob);
  const initialRetAge = dob && retDate ? Math.round((new Date(retDate).getTime() - new Date(dob).getTime()) / 31557600000) : 65;
  const [sliderAge, setSliderAge] = useState(initialRetAge);
  const minRetirementAge = dob ? getMinimumPensionAge(dob) : 55;

  return (
    <div className="mobile-person-card">
      <h3 className="mobile-person-card__title">
        {planType === 'JOINT' ? `Person ${personIndex + 1}` : 'Your Details'}
      </h3>

      <label className="mobile-label" htmlFor={`m-fullName-${personIndex}`}>Full Name *</label>
      <input id={`m-fullName-${personIndex}`} type="text" className={`mobile-input${errors.persons?.[personIndex]?.fullName ? ' mobile-input--error' : ''}`} placeholder="e.g., Jane Smith" {...register(`persons.${personIndex}.fullName`)} onBlur={onBlur} />
      {errors.persons?.[personIndex]?.fullName && <span className="mobile-error">{errors.persons[personIndex].fullName?.message}</span>}

      <label className="mobile-label" htmlFor={`m-dob-${personIndex}`}>Date of Birth *</label>
      <input id={`m-dob-${personIndex}`} type="date" className={`mobile-input${errors.persons?.[personIndex]?.dateOfBirth ? ' mobile-input--error' : ''}`} {...register(`persons.${personIndex}.dateOfBirth`)} onBlur={onBlur} />
      {dob && <span className="mobile-hint">Age: {age}</span>}

      <label className="mobile-label">Planned retirement age: {sliderAge}</label>
      <input type="range" min={minRetirementAge} max={75} value={sliderAge} className="mobile-slider" onChange={(e) => {
        const newAge = parseInt(e.target.value, 10);
        setSliderAge(newAge);
        const birth = new Date(dob);
        birth.setFullYear(birth.getFullYear() + newAge);
        const retDateStr = birth.toISOString().split('T')[0];
        void register(`persons.${personIndex}.retirementDate`).onChange({ target: { value: retDateStr } });
        onBlur();
      }} />
      <span className="mobile-hint">Date when you plan to stop working</span>

      {showPriorIncomeFields && (
        <>
          <span className="mobile-label">Total already received this tax year (£) — not a yearly rate</span>
          <span className="mobile-hint">
            Enter what you've actually received since 6 April in each category below, not an
            annual rate — we use the total to work out how much of your Personal Allowance and
            basic-rate band is already used. Leave any box at £0 if not applicable.
          </span>

          <label className="mobile-label" htmlFor={`m-prior-salary-${personIndex}`}>
            Salary (£)
          </label>
          <input id={`m-prior-salary-${personIndex}`} type="number" className={`mobile-input${errors.persons?.[personIndex]?.priorSalaryIncome ? ' mobile-input--error' : ''}`} {...register(`persons.${personIndex}.priorSalaryIncome`, { valueAsNumber: true })} onBlur={onBlur} />
          {errors.persons?.[personIndex]?.priorSalaryIncome && <span className="mobile-error">{errors.persons[personIndex].priorSalaryIncome?.message}</span>}

          <label className="mobile-label" htmlFor={`m-prior-drawdown-${personIndex}`}>
            Private pension income (£)
          </label>
          <input id={`m-prior-drawdown-${personIndex}`} type="number" className={`mobile-input${errors.persons?.[personIndex]?.priorDrawdownIncome ? ' mobile-input--error' : ''}`} {...register(`persons.${personIndex}.priorDrawdownIncome`, { valueAsNumber: true })} onBlur={onBlur} />
          {errors.persons?.[personIndex]?.priorDrawdownIncome && <span className="mobile-error">{errors.persons[personIndex].priorDrawdownIncome?.message}</span>}
          <span className="mobile-hint">
            Any taxable pension income other than the State Pension — drawdown, an annuity, a
            defined benefit (final salary) pension, or a lump sum you took as income.
          </span>

          <label className="mobile-label" htmlFor={`m-prior-state-pension-${personIndex}`}>
            State Pension already paid (£)
          </label>
          <input id={`m-prior-state-pension-${personIndex}`} type="number" className={`mobile-input${errors.persons?.[personIndex]?.priorStatePensionIncome ? ' mobile-input--error' : ''}`} {...register(`persons.${personIndex}.priorStatePensionIncome`, { valueAsNumber: true })} onBlur={onBlur} />
          {errors.persons?.[personIndex]?.priorStatePensionIncome && <span className="mobile-error">{errors.persons[personIndex].priorStatePensionIncome?.message}</span>}

          <label className="mobile-label" htmlFor={`m-prior-rental-${personIndex}`}>
            Rental income (£)
          </label>
          <input id={`m-prior-rental-${personIndex}`} type="number" className={`mobile-input${errors.persons?.[personIndex]?.priorRentalIncome ? ' mobile-input--error' : ''}`} {...register(`persons.${personIndex}.priorRentalIncome`, { valueAsNumber: true })} onBlur={onBlur} />
          {errors.persons?.[personIndex]?.priorRentalIncome && <span className="mobile-error">{errors.persons[personIndex].priorRentalIncome?.message}</span>}

          <span className="mobile-hint mobile-hint--subtotal">
            Subtotal (everything except dividends): {formatCurrency(nonDividendSubtotal, true)}
          </span>

          <label className="mobile-label" htmlFor={`m-prior-dividend-${personIndex}`}>
            Dividend income (£)
          </label>
          <input id={`m-prior-dividend-${personIndex}`} type="number" className={`mobile-input${errors.persons?.[personIndex]?.priorDividendIncome ? ' mobile-input--error' : ''}`} {...register(`persons.${personIndex}.priorDividendIncome`, { valueAsNumber: true })} onBlur={onBlur} />
          {errors.persons?.[personIndex]?.priorDividendIncome && <span className="mobile-error">{errors.persons[personIndex].priorDividendIncome?.message}</span>}
          <span className="mobile-hint">
            Kept separate from the total above — dividends use their own £500 tax-free
            allowance rather than stacking with your other income.
          </span>

          <span className="mobile-hint mobile-hint--subtotal">
            Total already received this tax year: {formatCurrency(totalPriorIncome, true)}
          </span>

          <label className="mobile-label" htmlFor={`m-prior-pension-contrib-${personIndex}`}>
            Pension contributions already made this tax year (£)
          </label>
          <input id={`m-prior-pension-contrib-${personIndex}`} type="number" className={`mobile-input${errors.persons?.[personIndex]?.priorPensionContributions ? ' mobile-input--error' : ''}`} {...register(`persons.${personIndex}.priorPensionContributions`, { valueAsNumber: true })} onBlur={onBlur} />
          {errors.persons?.[personIndex]?.priorPensionContributions && <span className="mobile-error">{errors.persons[personIndex].priorPensionContributions?.message}</span>}
          <span className="mobile-hint">
            Gross pension contributions already made since 6 April, including any tax relief added
            by your provider. Leave at £0 if not applicable.
          </span>
        </>
      )}

      {planAsOfDate && !priorIncomeAppliesToPlanYear && (
        <span className="mobile-hint">
          Your plan starts before 6 April, in the last few weeks of the current tax year, so
          anything received beforehand belongs to the tax year that's ending — not the one
          this plan covers. There's nothing to enter here.
        </span>
      )}

      <button type="button" className="mobile-expand-toggle" onClick={() => setExpandedPension(!expandedPension)}>
        {expandedPension ? '▾' : '▸'} State Pension details
      </button>
      {expandedPension && (
        <div className="mobile-expand-section">
          <label className="mobile-label" htmlFor={`m-spa-${personIndex}`}>State Pension Age *</label>
          <input id={`m-spa-${personIndex}`} type="number" className={`mobile-input${errors.persons?.[personIndex]?.statePensionAge ? ' mobile-input--error' : ''}`} {...register(`persons.${personIndex}.statePensionAge`, { valueAsNumber: true })} onBlur={onBlur} />
          {errors.persons?.[personIndex]?.statePensionAge && <span className="mobile-error">{errors.persons[personIndex].statePensionAge?.message}</span>}
          <span className="mobile-hint">
            {dob ? `Based on your DOB, likely ${getStatePensionAge(new Date(dob).getFullYear())}. ` : ''}
            <a href="https://www.gov.uk/check-state-pension-age" target="_blank" rel="noopener noreferrer">Check at gov.uk</a>
          </span>

          <label className="mobile-label" htmlFor={`m-spa-amount-${personIndex}`}>Your State Pension forecast (£/year) *</label>
          <input id={`m-spa-amount-${personIndex}`} type="number" className={`mobile-input${errors.persons?.[personIndex]?.currentStatePensionAnnual ? ' mobile-input--error' : ''}`} {...register(`persons.${personIndex}.currentStatePensionAnnual`, { valueAsNumber: true })} onBlur={onBlur} />
          {errors.persons?.[personIndex]?.currentStatePensionAnnual && <span className="mobile-error">{errors.persons[personIndex].currentStatePensionAnnual?.message}</span>}
          <span className="mobile-hint">Full new State Pension is {DEFAULT_STATE_PENSION_LABEL}.</span>
        </div>
      )}

      <button type="button" className="mobile-expand-toggle" onClick={() => setExpandedTaxFree(!expandedTaxFree)}>
        {expandedTaxFree ? '▾' : '▸'} Tax-free cash settings
      </button>
      {expandedTaxFree && (
        <div className="mobile-expand-section">
          <label className="mobile-label" htmlFor={`m-tfc-cap-${personIndex}`}>Lifetime Tax-Free Cash Limit (£)</label>
          <input id={`m-tfc-cap-${personIndex}`} type="number" className={`mobile-input${errors.persons?.[personIndex]?.taxFreeLumpSumCap ? ' mobile-input--error' : ''}`} {...register(`persons.${personIndex}.taxFreeLumpSumCap`, { valueAsNumber: true })} onBlur={onBlur} />
          {errors.persons?.[personIndex]?.taxFreeLumpSumCap && <span className="mobile-error">{errors.persons[personIndex].taxFreeLumpSumCap?.message}</span>}
          <span className="mobile-hint">Standard limit is £268,275.</span>

          <label className="mobile-label" htmlFor={`m-tfc-used-${personIndex}`}>Tax-Free Cash Already Taken (£)</label>
          <input id={`m-tfc-used-${personIndex}`} type="number" className={`mobile-input${errors.persons?.[personIndex]?.lsaUsedPrePlan ? ' mobile-input--error' : ''}`} {...register(`persons.${personIndex}.lsaUsedPrePlan`, { valueAsNumber: true })} onBlur={onBlur} />
          {errors.persons?.[personIndex]?.lsaUsedPrePlan && <span className="mobile-error">{errors.persons[personIndex].lsaUsedPrePlan?.message}</span>}

          <label className="mobile-label" htmlFor={`m-tfc-pct-${personIndex}`}>Take as Tax-Free Cash (%) *</label>
          <input id={`m-tfc-pct-${personIndex}`} type="number" step="0.01" className={`mobile-input${errors.persons?.[personIndex]?.taxFreeLumpSumPct ? ' mobile-input--error' : ''}`} {...register(`persons.${personIndex}.taxFreeLumpSumPct`, { valueAsNumber: true })} onBlur={onBlur} />
          {errors.persons?.[personIndex]?.taxFreeLumpSumPct && <span className="mobile-error">{errors.persons[personIndex].taxFreeLumpSumPct?.message}</span>}
          <span className="mobile-hint">Typically 25%, capped by your remaining lifetime allowance.</span>
        </div>
      )}
    </div>
  );
}

export default function Step2PeopleMobile({ initialData, onNext, onSave, onBack, lastSaved }: Readonly<Step2Props>) {
  const planType = initialData?.planType || 'SINGLE';
  const numPersons = planType === 'JOINT' ? 2 : 1;

  const defaultPersons = Array.from({ length: numPersons }, (_, i) => ({
    fullName: initialData?.persons?.[i]?.fullName || '',
    dateOfBirth: initialData?.persons?.[i]?.dateOfBirth || '',
    retirementDate: initialData?.persons?.[i]?.retirementDate || '',
    statePensionAge: initialData?.persons?.[i]?.statePensionAge || 67,
    currentStatePensionAnnual: initialData?.persons?.[i]?.currentStatePensionAnnual || DEFAULT_STATE_PENSION_ANNUAL,
    taxFreeLumpSumPct: initialData?.persons?.[i]?.taxFreeLumpSumPct || 25,
    taxFreeLumpSumCap: initialData?.persons?.[i]?.taxFreeLumpSumCap || 268275,
    lsaUsedPrePlan: initialData?.persons?.[i]?.lsaUsedPrePlan ?? 0,
    priorSalaryIncome: initialData?.persons?.[i]?.priorSalaryIncome ?? 0,
    // No `priorOtherTaxableIncome` here (phase 3, derived-total refactor) —
    // see Step2People.tsx's identical comment. seedPriorDrawdownIncome is a
    // one-way legacy migration (pre-split plan -> seed the total into
    // Drawdown), not a drift reconciliation — once the breakdown is saved,
    // the stored total is dropped entirely and never consulted again.
    priorDrawdownIncome: seedPriorDrawdownIncome(
      initialData?.persons?.[i]?.priorDrawdownIncome,
      initialData?.persons?.[i]?.priorOtherTaxableIncome
    ),
    priorStatePensionIncome: initialData?.persons?.[i]?.priorStatePensionIncome ?? 0,
    priorRentalIncome: initialData?.persons?.[i]?.priorRentalIncome ?? 0,
    priorDividendIncome: initialData?.persons?.[i]?.priorDividendIncome ?? 0,
    priorPensionContributions: initialData?.persons?.[i]?.priorPensionContributions ?? 0,
  }));

  const { register, handleSubmit, watch, formState: { errors } } = useForm<Step2FormData>({
    resolver: zodResolver(Step2Schema),
    defaultValues: { persons: defaultPersons },
  });

  const personsData = watch('persons');

  // A blanked currency box leaves react-hook-form holding NaN — scrub it to 0
  // before saving so JSON.stringify never turns it into a `null` that a later
  // reload can't tell apart from "explicitly cleared" (CRITICAL 2, #1592
  // phase 2 review round 2). See sanitizeNaN.ts. stripDerivedPriorIncomeTotal
  // additionally drops `priorOtherTaxableIncome` (phase 3, derived-total
  // refactor) — it must never be persisted alongside the breakdown.
  const handleBlur = () => onSave(stripDerivedPriorIncomeTotal(sanitizeNaN(watch())));

  return (
    <WizardMobileShell step={2} totalSteps={10} title="People" onBack={onBack} onContinue={() => handleSubmit((data) => onNext(stripDerivedPriorIncomeTotal(data)))()} lastSaved={lastSaved}>
      <div className="mobile-step-form">
        <p className="mobile-step-desc">We need some details to calculate your retirement projections</p>
        {Array.from({ length: numPersons }, (_, i) => (
          <PersonCard key={i} personIndex={i} planType={planType} personData={personsData[i]} register={register} errors={errors} onBlur={handleBlur} planAsOfDate={initialData?.planAsOfDate} />
        ))}
      </div>
    </WizardMobileShell>
  );
}
